// Pure helpers for the Mobile scans page: scan kinds, risk grade, and readable permissions / trackers from a report.

export const KINDS = [
  { value: 'mobile_static', label: 'Static analysis', desc: 'Reads the app file without running it: permissions, secrets, trackers, weak code.', time: 'About 10 minutes' },
  { value: 'mobile_package_vuln', label: 'Package vulnerabilities', desc: 'Finds libraries inside the app that have known security problems.', time: 'About 3 minutes' },
  { value: 'mobile_dynamic', label: 'Dynamic analysis', desc: 'Runs the app and watches what it does. Needs a device.', time: 'About 20 minutes' }
];
export const kindLabel = (type) => KINDS.find((k) => k.value === type)?.label || String(type || '-').replaceAll('_', ' ');

export const ANALYZERS = [['mobsf', 'MobSF'], ['apktool', 'APKTool'], ['jadx', 'JADX'], ['apkleaks', 'APKLeaks'], ['androguard', 'Androguard'], ['qark', 'QARK']];

// Options sent with a new scan.
export function scanBody({ targetId, kind, profile, sbom, tools }) {
  if (!targetId) throw new Error('This app has no uploaded file to scan.');
  const body = { target: targetId, scan_type: kind };
  if (kind === 'mobile_static') {
    if (!tools.length) throw new Error('Choose at least one analyser.');
    body.options = { analysis_profile: profile, generate_sbom: sbom, mobile_tools: tools };
  } else if (kind === 'mobile_package_vuln') body.options = { generate_sbom: true };
  return body;
}

// Same weights as the Target overview score: critical 8, high 5, medium 3, low 1, info 0.5.
export function appRisk(findings) {
  const closed = new Set(['false_positive', 'resolved', 'accepted_risk']);
  const open = findings.filter((f) => !closed.has(f.status));
  if (!open.length) return { score: 100, grade: 'A', counts: {} };
  const counts = {};
  open.forEach((f) => { counts[f.severity] = (counts[f.severity] || 0) + 1; });
  const w = (counts.critical || 0) * 8 + (counts.high || 0) * 5 + (counts.medium || 0) * 3 + (counts.low || 0) + (counts.info || 0) * 0.5;
  const score = Math.max(0, Math.min(100, Math.round((1 - w / (open.length * 8)) * 100)));
  return { score, grade: score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F', counts };
}
export const GRADE_COLOR = { A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', F: '#ef4444' };
export const GRADE_TEXT = { A: 'In good shape', B: 'Mostly fine', C: 'Some work needed', D: 'Needs work', F: 'Serious problems' };

const DANGEROUS = /(READ|SEND|RECEIVE|WRITE)_(SMS|CONTACTS|CALENDAR|CALL_LOG|EXTERNAL_STORAGE)|ACCESS_(FINE|COARSE|BACKGROUND)_LOCATION|CAMERA|RECORD_AUDIO|READ_PHONE_STATE|CALL_PHONE|BODY_SENSORS|PROCESS_OUTGOING_CALLS|ANSWER_PHONE_CALLS|READ_MEDIA_/;
const human = (name) => String(name).split('.').pop().toLowerCase().split('_').map((w, i) => (w.length <= 3 && i > 0 ? w.toUpperCase() : w)).join(' ').replace(/^./, (c) => c.toUpperCase());

// -> [{ name, raw, level: 'dangerous' | 'normal', info }], dangerous first. Accepts MobSF's object form or a plain list.
export function parsePermissions(report) {
  const src = report?.permissions;
  if (!src) return [];
  const rows = Array.isArray(src)
    ? src.map((p) => (typeof p === 'string' ? [p, {}] : [p.name || p.permission || '', p]))
    : Object.entries(src).map(([k, v]) => [k, v && typeof v === 'object' ? v : { status: String(v || '') }]);
  return rows.filter(([n]) => n).map(([raw, v]) => {
    const status = String(v.status || v.level || '').toLowerCase();
    const dangerous = status ? status === 'dangerous' : DANGEROUS.test(raw);
    return { raw, name: human(raw), level: dangerous ? 'dangerous' : 'normal', info: v.info || v.description || '' };
  }).sort((a, b) => (a.level === b.level ? a.name.localeCompare(b.name) : a.level === 'dangerous' ? -1 : 1));
}

// -> [names]. Accepts a list, or an object with a trackers list, or name-keyed entries.
export function parseTrackers(report) {
  const src = report?.trackers;
  if (!src) return [];
  const list = Array.isArray(src) ? src : Array.isArray(src.trackers) ? src.trackers : Object.entries(src).filter(([, v]) => v && typeof v === 'object').map(([k, v]) => v.name || k);
  return [...new Set(list.map((t) => (typeof t === 'string' ? t : t?.name)).filter(Boolean))];
}
